// src/pages/GiftCode.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  useTheme,
} from "@mui/material";
import TelegramIcon from "@mui/icons-material/Telegram";
import LanguageIcon from "@mui/icons-material/Language";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { useSelector } from "react-redux";
import PROMO_IMAGE_SRC from "./promoCode.png";

export default function GiftCode() {
  const theme = useTheme();
  const light = useSelector((state) => state?.theme?.light);
  const [giftCode, setGiftCode] = useState("");
  const [modalVisible, setModalVisible] = useState(false);

  // حالات التحقق
  const [touched, setTouched] = useState(false);
  const isEmpty = giftCode.trim().length === 0;
  const showError = touched && isEmpty;

  const colors = {
    background: light ? "#F9FAFB" : "#0B1120",
    contentBox: light ? "#FFFFFF" : "#111827",
    inputBg: light ? "#E5E7EB" : "#1F2937",
    inputBorder: light ? "#D1D5DB" : "#374151",
    textPrimary: light ? "#111827" : "#FFFFFF",
    textSecondary: light ? "#4B5563" : "#D1D5DB",
    textMuted: light ? "#6B7280" : "#9CA3AF",
    buttonBg: "#3B82F6",
    buttonText: "#FFFFFF",
    modalBg: light ? "#FFFFFF" : "#1F2937",
    modalText: light ? "#1F2937" : "#E5E7EB",
    modalTitle: "#F87171",
    cardBorder: light ? "#D1D5DB" : "#374151",
    cardBg: light ? "#F3F4F6" : "#1F2937",
    cardText: light ? "#1F2937" : "#E5E7EB",
  };

  const handleClaim = () => {
    // فعّل التحقق أولًا
    setTouched(true);
    if (giftCode.trim() === "") {
      // لا تفتح المودال إن كان الحقل فارغًا
      return;
    }

    // منطق التحقق الوهمي كما كان
    const isValid = false;
    if (!isValid) setModalVisible(true);
  };

  const openURL = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleEnter = (e) => {
    if (e.key === "Enter") handleClaim();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: colors.background,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pb: 8,
        gap: 3,
      }}
    >
      {/* الصورة العلوية */}
      <Box
        component="img"
        src={PROMO_IMAGE_SRC}
        alt="Promo Code"
        sx={{
          width: "100%",
          height: 250,
          objectFit: "fill",
          borderBottom: `1px solid ${colors.cardBorder}`,
        }}
      />

      {/* العنوان */}
      <Typography
        variant="h5"
        sx={{ color: colors.textPrimary, mt: 1, fontWeight: 700 }}
      >
        Enter Your Gift Code
      </Typography>

      {/* الإدخال وزر المطالبة */}
      <Stack spacing={2} sx={{ width: "90%", maxWidth: 720 }}>
        <TextField
          fullWidth
          required
          value={giftCode}
          onChange={(e) => setGiftCode(e.target.value)}
          onBlur={() => setTouched(true)}
          onKeyDown={handleEnter}
          placeholder="Type the gift code here"
          error={showError}
          helperText={showError ? "Please enter your gift code" : " "}
          FormHelperTextProps={{
            sx: {
              m: 0,
              pl: 1,
              color: showError ? theme.palette.error.main : colors.textMuted,
            },
          }}
          InputProps={{
            sx: {
              bgcolor: colors.inputBg,
              borderRadius: 2,
              color: colors.textPrimary,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: showError
                  ? theme.palette.error.main
                  : colors.inputBorder,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: showError
                  ? theme.palette.error.main
                  : colors.inputBorder,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: showError
                  ? theme.palette.error.main
                  : colors.inputBorder,
              },
            },
          }}
          inputProps={{
            "aria-label": "gift-code",
            style: { color: colors.textPrimary },
          }}
        />

        <Button
          onClick={handleClaim}
          variant="contained"
          disabled={isEmpty && touched} // اختياري: تعطيل الزر عند وجود خطأ
          sx={{
            bgcolor: colors.buttonBg,
            color: colors.buttonText,
            borderRadius: 2,
            py: 1.25,
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { bgcolor: colors.buttonBg },
          }}
        >
          Claim Reward
        </Button>
      </Stack>

      {/* صندوق معلومات */}
      <Card
        elevation={0}
        sx={{
          width: "90%",
          maxWidth: 720,
          bgcolor: colors.contentBox,
          borderRadius: 2,
          border: `1px solid ${colors.cardBorder}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{ color: colors.textPrimary, fontWeight: "bold", mb: 1.5 }}
          >
            🎁 What is the Gift Code?
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: colors.textSecondary, lineHeight: 1.7 }}
          >
            Every 24 hours, we publish a new Gift Code on our Telegram channel.
            The first 25 users to redeem the code will receive random crypto
            rewards ranging from 0 to 100 USDT or other surprise tokens.
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.textMuted, mt: 1.5, lineHeight: 1.7 }}
          >
            Be quick! The earlier you redeem, the better your chances of getting
            a reward.
          </Typography>
        </CardContent>
      </Card>

      {/* قسم المتابعة */}
      <Box sx={{ width: "90%", maxWidth: 720 }}>
        <Typography
          variant="body1"
          align="center"
          sx={{ color: colors.textPrimary, mb: 2 }}
        >
          📢 Follow us on Telegram to get the daily Gift Code. It’s posted every
          day at 00:00 UTC:
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Card
              onClick={() => openURL("https://t.me/One_CryptoX_Official")}
              elevation={0}
              sx={{
                cursor: "pointer",
                borderRadius: 2,
                border: `1px solid ${colors.cardBorder}`,
                bgcolor: colors.cardBg,
                p: 2.5,
                textAlign: "center",
                transition: "transform 0.15s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
            >
              <TelegramIcon sx={{ fontSize: 40, color: "#229ED9" }} />
              <Typography
                variant="subtitle2"
                sx={{ mt: 1.25, color: colors.cardText, fontWeight: 600 }}
              >
                Telegram
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card
              onClick={() => openURL("https://1cryptox.com/")}
              elevation={0}
              sx={{
                cursor: "pointer",
                borderRadius: 2,
                border: `1px solid ${colors.cardBorder}`,
                bgcolor: colors.cardBg,
                p: 2.5,
                textAlign: "center",
                transition: "transform 0.15s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
            >
              <LanguageIcon sx={{ fontSize: 40, color: "#10B981" }} />
              <Typography
                variant="subtitle2"
                sx={{ mt: 1.25, color: colors.cardText, fontWeight: 600 }}
              >
                Website
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* قسم السجل */}
      <Card
        elevation={0}
        sx={{
          width: "90%",
          maxWidth: 720,
          bgcolor: colors.contentBox,
          borderRadius: 2,
          border: `1px solid ${colors.cardBorder}`,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Typography
            variant="h6"
            sx={{ color: colors.textPrimary, fontWeight: 700, mb: 2 }}
          >
            Claim History
          </Typography>

          <Box
            sx={{
              border: `1px solid ${colors.inputBorder}`,
              bgcolor: colors.inputBg,
              borderRadius: 1.5,
              py: 3.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <HistoryOutlinedIcon
              sx={{ fontSize: 40, color: colors.textMuted }}
            />
            <Typography
              variant="body1"
              sx={{ color: colors.textMuted, mt: 1.5 }}
            >
              No records yet
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* المودال */}
      <Dialog
        open={modalVisible}
        onClose={() => setModalVisible(false)}
        PaperProps={{
          sx: {
            bgcolor: colors.modalBg,
            borderRadius: 2.5,
            minWidth: { xs: "80%", sm: 420 },
          },
        }}
      >
        <DialogTitle sx={{ color: colors.modalTitle, fontWeight: 700 }}>
          Invalid or Used Code
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: colors.modalText }}>
            The gift code you entered is invalid or has already reached its
            redemption limit
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setModalVisible(false)}
            variant="contained"
            sx={{
              bgcolor: colors.buttonBg,
              color: colors.buttonText,
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              "&:hover": { bgcolor: colors.buttonBg },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
